import React from 'react'
import InputFill from '../form/InputFill'
import Textarea from '../form/Textarea'
import InputSelect from '../form/InputSelect'

import { useTranslation } from 'react-i18next'
import { DELIVERY_TIME, ORDER_METHOD, PAYMENT_METHOD, VOUCHER_TYPE_PERCENT } from '../../constants'
import { getDeliveryTime } from '../../utils'
import { useSelector } from 'react-redux'
import { paymentMethodsSupport as paymentMethodsSupportSelector } from '../../selectors/restaurant'

import IconPaypalActive from '../../assets/icons/IconPaypalActive'
import IconCashActive from '../../assets/icons/IconCashActive'
import PaymentMethodGroup from '../common/PaymentMethodGroup'
import Button from '../elements/Button'
import IconCashCard from '../../assets/icons/IconCashCard'
import IconCheck from '../../assets/icons/IconCheck'
import IconSearch from '../../assets/icons/IconSearch'
import FormatCurrency from '../elements/FormatCurrency'
// import IconEC from '../../assets/icons/pay_ec.png'

const CustomerInfo = ({
    handleSubmit,
    form,
    handleInputChange,
    clearErrorField,
    errors,
    handleAddressBlur,
    listAddress,
    onVerifyVoucher,
    voucherVerified,
    voucherVerifying,
    voucherData,
    voucherError,
}) => {
    const { t } = useTranslation()
    const { orderMethod } = useSelector(state => state.OrderReducer)
    const paymentMethodsSupport = useSelector(paymentMethodsSupportSelector)

    const listDeliveryTimes = getDeliveryTime()

    const fields = [
        {
            label: 'cartMobile.lbCustomerName',
            name: 'userName',
            props: {
                required: true,
            },
        },
        {
            label: 'customerInfo.phone',
            name: 'userPhone',
            props: {
                numberOnly: true,
                required: true,
            },
        },
        listAddress?.length && {
            label: 'customerInfo.lbSelectAddress',
            name: 'selectAddress',
            component: InputSelect,
            props: {
                options: [
                    <option key="opt-default" value={-1}>
                        {t('customerInfo.lbDefaultSelectAddress')}
                    </option>,
                    ...listAddress.map((address, index) => (
                        <option key={index} value={index}>
                            {[
                                `${address.street} ${address.addressNumber}`,
                                `${address.zipCode} ${address.city}`,
                                address.floor,
                            ]
                                .filter(Boolean)
                                .join(', ')}
                        </option>
                    )),
                ],
            },
        },
        {
            label: 'customerInfo.postCode',
            name: 'userPostcode',
            props: {
                onBlur: () => handleAddressBlur(),
                required: true,
            },
        },
        {
            label: 'customerInfo.street',
            name: 'userStreet',
            props: {
                onBlur: () => handleAddressBlur(),
                required: true,
            },
        },
        {
            label: 'customerInfo.apartmentNumber',
            name: 'userAddressNumber',
            props: {
                onBlur: () => handleAddressBlur(),
                required: true,
            },
        },
        {
            label: 'customerInfo.city',
            name: 'userCity',
            props: {
                onBlur: () => handleAddressBlur(),
                required: true,
            },
        },
        {
            label: 'customerInfo.floor',
            name: 'floor',
            props: {},
        },
        {
            label: 'customerInfo.office',
            name: 'userOfficeName',
            props: {},
        },
        {
            label: 'customerInfo.email',
            name: 'userEmail',
            props: {},
        },
        {
            label:
                orderMethod === ORDER_METHOD.DELIVERY
                    ? 'customerInfo.deliveryTime'
                    : 'customerInfo.pickupTime',
            name: 'deliveryTime',
            component: InputSelect,
            props: {
                options: listDeliveryTimes.map((time, index) => (
                    <option key={index} value={time}>
                        {time === DELIVERY_TIME
                            ? t('cartMobile.lbSelectDeliverTime')
                            : time}
                    </option>
                )),
            },
        },
        {
            label: 'cartMobile.lbNote',
            name: 'userAddressNote',
            component: Textarea,
        },
        !!paymentMethodsSupport.length && {
            label: 'customerInfo.paymentMethod',
            name: 'paymentMethod',
            component: PaymentMethodGroup,
            props: {
                list: [
                    {
                        label: t('customerInfo.paymentMethodGroupCash'),
                        options: [
                            paymentMethodsSupport.includes(PAYMENT_METHOD.CASH) && {
                                widthIcon: true,
                                label: t('customerInfo.cash'),
                                value: PAYMENT_METHOD.CASH,
                                icon: IconCashActive,
                                unCheckClass: 'payment-type-uncheck',
                            },
                            paymentMethodsSupport.includes(PAYMENT_METHOD.CASH_CARD) && {
                                widthIcon: true,
                                label: t('customerInfo.cash_card'),
                                value: PAYMENT_METHOD.CASH_CARD,
                                icon: IconCashCard,
                                unCheckClass: 'payment-type-uncheck',
                            },
                        ].filter(Boolean)
                    },
                    {
                        label: t('customerInfo.paymentMethodGroupOnline'),
                        options: [
                            paymentMethodsSupport.includes(PAYMENT_METHOD.PAYPAL) && {
                                widthIcon: true,
                                label: 'Paypal',
                                value: PAYMENT_METHOD.PAYPAL,
                                icon: IconPaypalActive,
                                unCheckClass: 'payment-type-uncheck',
                            },
                        ].filter(Boolean)
                    },
                ].filter(el => !!el.options.length),
            },
        },
    ].filter(Boolean)

    return (
        <form onSubmit={handleSubmit}>
            <div className="row">
                {fields.filter(Boolean).map((field, index) => {
                    const Component = field.component || InputFill
                    return (
                        <div key={index} className="col-12 col-md-6">
                            <Component
                                id={`cusInfo-${field.name}`}
                                className="mt-6"
                                label={t(field.label)}
                                name={field.name}
                                value={form[field.name]}
                                error={errors[field.name]}
                                onChange={handleInputChange}
                                onFocus={() => clearErrorField(field.name)}
                                {...field.props}
                            />
                        </div>
                    )
                })}
            </div>
            <div className="d-flex">
                <div style={{ flex: 1, minWidth: 0 }}>
                    <InputFill
                        id="cusInfo-discountCode"
                        className="mt-6"
                        label={t('voucher.discountCode')}
                        name="discountCode"
                        value={form.discountCode ?? ''}
                        onChange={handleInputChange}
                        onFocus={() => clearErrorField('discountCode')}
                        inputStyle={{
                            borderColor: voucherError ? '#FF0000' : '#CDCDCD',
                        }}
                    />
                </div>
                <div className="d-flex align-items-end" style={{ flexShrink: 0 }}>
                    {voucherVerified ? (
                        <Button outline disabled large style={{ padding: '4px 12px 0' }}>
                            <IconCheck width={20} height={20} fill="green" />
                        </Button>
                    ) : (
                        <Button
                            onClick={onVerifyVoucher}
                            disabled={voucherVerifying || !form.discountCode?.trim()}
                            large
                            style={{ padding: '4px 12px 0' }}
                        >
                            <IconSearch width={20} height={20} fill="#fff" />
                        </Button>
                    )}
                </div>
            </div>
            {voucherError && (
                <div className="voucher-error">{voucherError}</div>
            )}
            {voucherData && (
                <div className="voucher-details">
                    <div className="voucher-details__row">
                        <span className="voucher-details__name">{voucherData.code}</span>
                        <span className="voucher-details__value">
                            {voucherData.type === VOUCHER_TYPE_PERCENT
                                ? `${voucherData.value}%`
                                : <FormatCurrency amount={voucherData.value} />}
                        </span>
                    </div>
                </div>
            )}
        </form>
    )
}

export default CustomerInfo
